import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { LinkOutlined, DeleteOutlined } from '@ant-design/icons';

import Modal from 'common/components/modal';
import { IconClock } from 'common/components/svgs';
import ScheduleModal from './schedule';

import { DRAFT } from './index';

const useStyles = makeStyles(theme => ({
  wrapper: {
    '& .ant-modal-body': {
      padding: '40px 32px',
    },
  },
  draftContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 34,
    padding: '0 12px',
    marginBottom: 16,
    color: theme.colors.blue6,
    backgroundColor: theme.colors.blue1,
  },
  draftWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  dateText: {
    paddingLeft: 6,
    ...theme.typography.body2,
  },
  editText: {
    ...theme.typography.body2,
    color: theme.colors.mainGreen6,
    cursor: 'pointer',
  },
  title: {
    ...theme.typography.h4,
    color: theme.colors.titleColor,
    marginBottom: 8,
  },
  content: {
    ...theme.typography.body2,
    color: theme.colors.captionColor,
    marginBottom: 16,
  },
  article: {
    ...theme.typography.body2,
    padding: '8px 16px',
    backgroundColor: theme.colors.gray2,
    boxShadow: 'inset 2px 0px 0px rgba(0, 0, 0, 0.09)',
    borderRadius: '0px 2px 2px 0px',
    marginBottom: 16,

    '& > p': {
      marginBottom: 0,
      color: theme.colors.disableColor,
    },
  },
  articleTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  articleTitleText: {
    maxWidth: 'calc(100% - 24px)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: theme.colors.mainGreen6,
    cursor: 'pointer',
  },
  articleLinkIcon: {
    color: theme.colors.disableColor,
    paddingLeft: 8,

    '& > span': {
      color: theme.colors.captionColor,
    },
  },
  footer: {
    ...theme.typography.body2,
    color: theme.colors.red11,
  },
  deleteText: {
    paddingLeft: 6,
    cursor: 'pointer',
  },
}));

const ViewReleaseNote = forwardRef(({ handleChangeSchedule, handleRemoveClick }, ref) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);
  const [currRow, setCurrRow] = useState({});

  const scheduleModalRef = useRef(null);

  const handleCancelModal = () => {
    setVisible(false);

    if (scheduleModalRef.current.visible) {
      scheduleModalRef.current.handleCloseModal();
    }
  };

  const handleEditClick = () => {
    scheduleModalRef.current?.handleOpenModal();
  };

  useImperativeHandle(ref, () => ({
    visible,
    handleOpenModal: row => {
      setVisible(true);
      setCurrRow(row);
    },
    handleCloseModal: handleCancelModal,
  }));

  return (
    <Modal
      width={446}
      visible={visible}
      footer={null}
      wrapClassName={classes.wrapper}
      onCancel={handleCancelModal}
    >
      <div className={classes.container}>
        <If condition={currRow?.status === DRAFT}>
          <div className={classes.draftContainer}>
            <div className={classes.draftWrapper}>
              <IconClock />
              <span className={classes.dateText}>{`${t('content.release_note.draft.text')} ${
                currRow?.publishedAt
              }`}</span>
            </div>

            <span className={classes.editText} onClick={handleEditClick}>
              {t('content.release_note.modal.view.edit')}
            </span>
          </div>
        </If>

        <h3 className={classes.title}>{currRow?.title}</h3>

        <p className={classes.content}>{currRow?.content}</p>

        <If condition={currRow.article}>
          <div className={classes.article}>
            <p>{t('content.release_note.modal.view.linked_article')}</p>
            <div className={classes.articleTitle}>
              <span className={classes.articleTitleText}>{currRow.article?.title}</span>
              <span className={classes.articleLinkIcon}>
                (<LinkOutlined />)
              </span>
            </div>
          </div>
        </If>

        <div className={classes.footer} onClick={() => handleRemoveClick(currRow?.id)}>
          <DeleteOutlined />
          <span className={classes.deleteText}>{t('content.release_note.btn.delete')}</span>
        </div>

        <ScheduleModal
          wrappedComponentRef={form => (scheduleModalRef.current = form)}
          params={{ id: currRow?.id }}
          handleSuccessCallback={handleChangeSchedule}
        />
      </div>
    </Modal>
  );
});

export default ViewReleaseNote;
